import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Users, Shield, Heart, MessageCircle } from "lucide-react";

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      if (user.profile_completed) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_5d44d435-f945-4d83-b040-1323b7b25dbc/artifacts/7ra9fmgb_Artboard%2012_4%403x.png" 
          alt="WOMBS" 
          className="h-8 opacity-50 animate-pulse"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="min-h-screen flex flex-col lg:flex-row">
        
        {/* Left side - Image */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img 
            src="https://allwombs.com/_assets/media/6bdacf91e595c487556bfbb8a5db05a2.jpg" 
            alt="Motherhood" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-sage/10" />
        </div>

        {/* Right side - Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 lg:w-1/2">
          <div className="max-w-sm w-full">
            
            {/* Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_5d44d435-f945-4d83-b040-1323b7b25dbc/artifacts/7ra9fmgb_Artboard%2012_4%403x.png" 
              alt="WOMBS" 
              className="h-8 mb-10"
            />

            {/* Headline */}
            <h1 className="text-lg font-serif text-sage-darker mb-4">
              You&apos;re not meant to do motherhood alone.
            </h1>
            
            {/* Subtext */}
            <p className="text-xs text-muted-foreground leading-relaxed mb-8">
              Whether you&apos;re trying, pausing, postpartum, grieving, or simply confused—
              this is a space built for where you are, not where you&apos;re expected to be.
            </p>

            {/* Value Props - 2x2 Grid with icons */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-sage" />
                <div>
                  <p className="text-xs text-sage-dark font-medium">Connection</p>
                  <p className="text-[10px] text-muted-foreground">with women who get it</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-sage" />
                <div>
                  <p className="text-xs text-sage-dark font-medium">Community</p>
                  <p className="text-[10px] text-muted-foreground">safe and non-judgemental</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-sage" />
                <div>
                  <p className="text-xs text-sage-dark font-medium">Care</p>
                  <p className="text-[10px] text-muted-foreground">from trusted experts</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-sage" />
                <div>
                  <p className="text-xs text-sage-dark font-medium">Conversations</p>
                  <p className="text-[10px] text-muted-foreground">honest, real, supportive</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button 
              onClick={handleGoogleLogin}
              className="w-full bg-sage text-white hover:bg-sage-dark rounded-full h-10 text-xs font-medium"
            >
              Sign up now
            </Button>

            {/* Footer links */}
            <div className="mt-10 text-[10px] text-muted-foreground/60 flex gap-3">
              <a href="/terms" className="hover:text-sage-dark">Terms</a>
              <span>·</span>
              <a href="/privacy" className="hover:text-sage-dark">Privacy</a>
            </div>
          </div>
        </div>

        {/* Mobile image - shown only on small screens */}
        <div className="lg:hidden w-full h-48 relative">
          <img 
            src="https://allwombs.com/_assets/media/6bdacf91e595c487556bfbb8a5db05a2.jpg" 
            alt="Motherhood" 
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default Landing;
